<script lang="ts">
  import { Mesh, Object3D, type Object3DEventMap } from 'three'
  import useObjectQueries from '../../../hooks/useObjectQueries'
  import InputLabel from '../InputLabel.svelte'
  import TranslationInput from '../../../../../components/translation-input/TranslationInput.svelte'
  import Button from '../../../../../components/button/Button.svelte'
  import {
    convertToSceneUnit,
    convertToUserUnit,
  } from '../../../../../helpers/scene-json'
  import { makeExtrudedPolygonGeometry } from '../../../../../helpers/three'
  import { updateSelectionBox } from '../../../../../helpers/selection-material'
  import { ThreeItemKeys } from '../../../../../constants/three'
  import { debounce } from 'es-toolkit'
  import i18n from '../../../../../i18n'

  const { object }: { object: Object3D<Object3DEventMap> } = $props()

  const { useObject, invalidateCanvas, removeMutation } = useObjectQueries()
  const data = $derived(useObject(object.uuid))

  const unit = 'mm'

  type Point = [number, number]

  function getPoints(selectedObject?: Object3D): Point[] {
    return (
      (selectedObject?.userData[ThreeItemKeys.FORBIDDEN_ZONE_POINTS] as
        | Point[]
        | undefined) ?? []
    )
  }

  function getHeight(selectedObject?: Object3D): number {
    return (
      (selectedObject?.userData[ThreeItemKeys.FORBIDDEN_ZONE_HEIGHT] as
        | number
        | undefined) ?? 0
    )
  }

  const height = $derived(getHeight(data.data))

  const throttledInvalidateCanvas = debounce(() => {
    invalidateCanvas()
  }, 3000)

  function updateHeight(value: number) {
    if (!(data.data instanceof Mesh)) return

    const newHeight = convertToSceneUnit(value) as number
    data.data.userData[ThreeItemKeys.FORBIDDEN_ZONE_HEIGHT] = newHeight
    data.data.geometry.dispose()
    data.data.geometry = makeExtrudedPolygonGeometry(
      getPoints(data.data).map(([x, y]) => ({ x, y })),
      newHeight,
    )
    updateSelectionBox(data.data)
    throttledInvalidateCanvas()
  }

  function deleteZone() {
    removeMutation.mutate(object.uuid)
  }
</script>

{#key data.dataUpdatedAt}
  {#if data.data}
    <div class="height-container">
      <InputLabel>{$i18n.t('project.rightBar.forbiddenZone.height')}</InputLabel
      >
      <TranslationInput
        name="height"
        onarrowpressed={updateHeight}
        ontype={updateHeight}
        {unit}
        defaultValue={convertToUserUnit(height)}
        onblur={invalidateCanvas}
      />
    </div>
    <Button
      class="delete-button"
      onclick={deleteZone}
      size="sm"
      variant="destructive"
    >
      {$i18n.t('project.rightBar.forbiddenZone.delete')}
    </Button>
  {/if}
{/key}

<style lang="scss">
  .height-container {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  :global(.delete-button) {
    width: 100%;
    margin-top: 0.75rem;
  }
</style>
